/**
 * Browser localStorage utilities for API keys, creators, and session data
 * Replaces database persistence with client-side storage
 */

export interface StoredApiKeys {
  apifyToken: string | null;
  openaiToken: string | null;
}

export interface Creator {
  id: string;
  name: string;
  platforms: {
    youtube?: string[];
    instagram?: string[];
    tiktok?: string[];
    linkedin?: string[];
  };
  notes?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface SessionData {
  scrapeResults?: unknown;
  analysis?: unknown;
  ideas?: unknown;
  timestamp: string;
}

const STORAGE_KEYS = {
  APIFY_TOKEN: 'ai_brand_lab_apify_token',
  OPENAI_TOKEN: 'ai_brand_lab_openai_token',
  CREATORS: 'ai_brand_lab_creators',
  CURRENT_SESSION: 'ai_brand_lab_current_session',
  LAST_BACKUP: 'ai_brand_lab_last_backup',
} as const;

/**
 * API Keys Storage
 */
export const apiKeyStorage = {
  save: (apifyToken: string, openaiToken: string): void => {
    try {
      localStorage.setItem(STORAGE_KEYS.APIFY_TOKEN, apifyToken);
      localStorage.setItem(STORAGE_KEYS.OPENAI_TOKEN, openaiToken);
    } catch (error) {
      console.error('Failed to save API keys to localStorage:', error);
      throw new Error('Unable to save API keys. Please check your browser settings.');
    }
  },

  load: (): StoredApiKeys => {
    try {
      return {
        apifyToken: localStorage.getItem(STORAGE_KEYS.APIFY_TOKEN),
        openaiToken: localStorage.getItem(STORAGE_KEYS.OPENAI_TOKEN),
      };
    } catch (error) {
      console.error('Failed to load API keys from localStorage:', error);
      return { apifyToken: null, openaiToken: null };
    }
  },

  clear: (): void => {
    try {
      localStorage.removeItem(STORAGE_KEYS.APIFY_TOKEN);
      localStorage.removeItem(STORAGE_KEYS.OPENAI_TOKEN);
    } catch (error) {
      console.error('Failed to clear API keys from localStorage:', error);
    }
  },

  hasKeys: (): boolean => {
    const { apifyToken, openaiToken } = apiKeyStorage.load();
    return Boolean(apifyToken && openaiToken);
  },
};

/**
 * Creators Storage
 */
export const creatorsStorage = {
  save: (creators: Creator[]): void => {
    try {
      localStorage.setItem(STORAGE_KEYS.CREATORS, JSON.stringify(creators));
    } catch (error) {
      console.error('Failed to save creators to localStorage:', error);
      throw new Error('Unable to save creators. Please check your browser settings.');
    }
  },

  load: (): Creator[] => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.CREATORS);
      if (!stored) return [];
      return JSON.parse(stored) as Creator[];
    } catch (error) {
      console.error('Failed to load creators from localStorage:', error);
      return [];
    }
  },

  add: (creator: Omit<Creator, 'id' | 'createdAt' | 'updatedAt'>): Creator => {
    const creators = creatorsStorage.load();
    const newCreator: Creator = {
      ...creator,
      id: crypto.randomUUID(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    creators.push(newCreator);
    creatorsStorage.save(creators);
    return newCreator;
  },

  update: (id: string, updates: Partial<Omit<Creator, 'id' | 'createdAt'>>): Creator | null => {
    const creators = creatorsStorage.load();
    const index = creators.findIndex((c) => c.id === id);
    if (index === -1) return null;

    const updated: Creator = {
      ...creators[index],
      ...updates,
      updatedAt: new Date().toISOString(),
    };
    creators[index] = updated;
    creatorsStorage.save(creators);
    return updated;
  },

  delete: (id: string): boolean => {
    const creators = creatorsStorage.load();
    const filtered = Array.isArray(creators) ? creators.filter((c) => c.id !== id) : [];
    if (filtered.length === creators.length) return false;
    creatorsStorage.save(filtered);
    return true;
  },

  clear: (): void => {
    try {
      localStorage.removeItem(STORAGE_KEYS.CREATORS);
    } catch (error) {
      console.error('Failed to clear creators from localStorage:', error);
    }
  },

  count: (): number => {
    return creatorsStorage.load().length;
  },
};

/**
 * Session Storage (temporary data for current workflow)
 */
export const sessionStorage = {
  save: (data: Partial<SessionData>): void => {
    try {
      const current = sessionStorage.load();
      const updated = {
        ...current,
        ...data,
        timestamp: new Date().toISOString(),
      };
      localStorage.setItem(STORAGE_KEYS.CURRENT_SESSION, JSON.stringify(updated));
    } catch (error) {
      console.error('Failed to save session data:', error);
    }
  },

  load: (): SessionData => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.CURRENT_SESSION);
      if (!stored) {
        return { timestamp: new Date().toISOString() };
      }
      return JSON.parse(stored) as SessionData;
    } catch (error) {
      console.error('Failed to load session data:', error);
      return { timestamp: new Date().toISOString() };
    }
  },

  clear: (): void => {
    try {
      localStorage.removeItem(STORAGE_KEYS.CURRENT_SESSION);
    } catch (error) {
      console.error('Failed to clear session data:', error);
    }
  },
};

/**
 * Backup/Restore functionality
 */
export interface BackupData {
  version: string;
  exportDate: string;
  apiKeys: StoredApiKeys;
  creators: Creator[];
  session: SessionData;
}

export const backupStorage = {
  create: (): BackupData => {
    return {
      version: '1.0',
      exportDate: new Date().toISOString(),
      apiKeys: apiKeyStorage.load(),
      creators: creatorsStorage.load(),
      session: sessionStorage.load(),
    };
  },

  restore: (backup: BackupData): void => {
    try {
      // Restore API keys
      if (backup.apiKeys.apifyToken && backup.apiKeys.openaiToken) {
        apiKeyStorage.save(backup.apiKeys.apifyToken, backup.apiKeys.openaiToken);
      }

      // Restore creators
      if (backup.creators && Array.isArray(backup.creators)) {
        creatorsStorage.save(backup.creators);
      }

      // Restore session (optional)
      if (backup.session) {
        sessionStorage.save(backup.session);
      }

      // Update last backup timestamp
      localStorage.setItem(STORAGE_KEYS.LAST_BACKUP, new Date().toISOString());
    } catch (error) {
      console.error('Failed to restore backup:', error);
      throw new Error('Unable to restore backup data. Please check the file format.');
    }
  },

  download: (): void => {
    const backup = backupStorage.create();
    const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `ai-brand-lab-backup-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  },

  getLastBackupDate: (): string | null => {
    try {
      return localStorage.getItem(STORAGE_KEYS.LAST_BACKUP);
    } catch {
      return null;
    }
  },
};

/**
 * Clear all app data
 */
export const clearAllData = (): void => {
  apiKeyStorage.clear();
  creatorsStorage.clear();
  sessionStorage.clear();
  localStorage.removeItem(STORAGE_KEYS.LAST_BACKUP);
};
